<template>
  <div>
    <h1 class="title ml-4 pl-4">{{ $t("asset-group.list-view") }}</h1>
    <b-dropdown split split-href="#" :text="$t('task.download-options')" size="sm" variant="light" class="mr-4 text-right float-right">
      <b-dropdown-item @click="downloadExcel" href="#">{{ $t("common.download") }}</b-dropdown-item>
    </b-dropdown>
    <div class="float-left pt-4">
      <b-modal :id="'modal-add-asset-group-count'" centered :title="$t('asset-group.count')" hide-footer>
        <div>
          <input class="form-control" type="number" min="0" v-model.number="assetGroupCount" />
          <div class="text-right">
            <sweet-button @click="saveAssetGroupCount()" :disabled="$v.assetGroupCount.$invalid" variant="success" icon="mdi mdi-check" size="sm" buttonClass="ml-2 mt-4">
              {{ $t("common.save") }}
            </sweet-button>
          </div>
        </div>
      </b-modal>

      <b-modal :id="'modal-add-asset-group-description'" size="lg" centered :title="$t('asset-group.description')" hide-footer>
        <div>
          <sweet-textarea
            v-model="assetGroupDescription"
            :isValid="!$v.assetGroupDescription.$error"
            @input="$v.assetGroupDescription.$touch()"
            type="text"
            layout="vertical"
            :placeholder="$t('common.description')"
            :label="$t('asset-group.description')"
            :rows="9"
          />

          <div class="text-right">
            <sweet-button
              @click="saveAssetGroupDescription()"
              :invalid="$v.assetGroupDescription.$invalid"
              variant="success"
              icon="mdi mdi-check"
              size="sm"
              buttonClass="ml-2 mt-4"
            >
              {{ $t("common.save") }}
            </sweet-button>
          </div>
        </div>
      </b-modal>
      <div>
        <table style="width: 100%;">
          <thead>
            <tr>
              <th>{{ $t("asset-group-category.text") }}</th>
              <th>{{ $t("asset-group.no") }}</th>
              <th>{{ $t("asset-group.name") }}</th>

              <th>{{ $t("asset-group.count") }}</th>
              <th>{{ $t("asset-group.admin") }}</th>
              <th>{{ $t("asset-group.description") }}</th>
              <th>{{ $t("asset-group-category.admin") }}</th>
              <th>
                <a href="javascript:;" @click="toggleFilter">
                  <span v-if="filterEnabled" class="text-warning">
                    <i class="bx bxs-filter-alt"></i>
                    {{ $t("common.filters-hide") }}
                  </span>
                  <span v-else class="text-muted">
                    <i class="bx bxs-filter-alt"></i>
                    {{ $t("common.filters-show") }}
                  </span>
                </a>
              </th>
            </tr>
            <tr v-if="filterEnabled">
              <th>
                <filter-select
                  id="assetGroupCategoryFilter"
                  ref="assetGroupCategoryFilter"
                  :placeholder="$t('asset-group-category.text')"
                  :multiple="false"
                  @filter="onAssetGroupCategorySearch"
                  :selected="assetGroupCategoryFilter"
                  @changed="onAssetGroupCategorySelect"
                />
              </th>
              <th><input :placeholder="$t('task.no-in-short')" class="form-control" v-model="noFilter" type="text" /></th>
              <th><input class="form-control " v-model="nameFilter" :placeholder="$t('common.search')" /></th>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody v-if="pagination && pagination.docs.length">
            <tr v-for="item in pagination.docs" :key="item._id" class="hider">
              <td class="gray-text w-15">
                <span v-if="item.assetGroupCategory">{{ item.assetGroupCategory.name | translate }}</span>
              </td>

              <td class="w-5">
                <div class="d-flex" style="gap: 1rem;">
                  <span>{{ item.no }}</span>
                </div>
              </td>

              <td class="gray-text w-15">
                <span>{{ item.name | translate }}</span>
              </td>
              <td>
                <span>{{ item.count ? item.count : "-" }}</span>
              </td>
              <td class="w-10">
                <div class="d-flex align-items-center">
                  <span v-if="item.admins">{{ item.admins[0].account.name ? item.admins[0].account.name : "-" }}</span>
                </div>
              </td>
              <td class="w-30">
                <span v-if="item.description">{{ item.description | translate }}</span>
                <span v-else> - </span>
              </td>
              <td class="w-10">
                <span v-if="item.assetGroupCategory">
                  <span v-if="item.assetGroupCategory.admins.length > 0">
                    {{ item.assetGroupCategory.admins[0].account.name ? item.assetGroupCategory.admins[0].account.name : "-" }}
                  </span>
                  <span v-else>
                    -
                  </span>
                </span>
              </td>
              <td class="w-10">
                <div class="hidden">
                  <span v-b-tooltip.hover :title="$t('asset-group.edit-count')" class="hand text-success mr-1" @click="editAssetGroupCount(item)">
                    <i class="bx bx-pencil align-middle"></i
                  ></span>
                  <span v-b-tooltip.hover :title="$t('asset-group.edit-description')" class="hand text-main mr-1" @click="editAssetGroupDescription(item)">
                    <i class="bx bx-pencil align-middle"></i
                  ></span>
                  <span v-b-tooltip.hover :title="$t('asset-group-survey.view-result')" class="hand text-primary mb-1" @click="redirectToSurvey(item)">
                    <i class="bx bx-log-in-circle font-size-20 align-middle"></i
                  ></span>
                </div>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="8">
                <empty v-if="!isLoading && (!pagination || pagination.docs.length == 0)">
                  <h5 class="text-center">
                    {{ $t("asset-group.empty") }}
                  </h5>
                </empty>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="pagination" class="row mt-4">
          <div class="col">
            <ul class="pagination pagination-rounded mb-0">
              <b-pagination v-model="request.options.page" :total-rows="pagination.totalDocs" :per-page="pagination.limit" limit="10" size="sm"></b-pagination>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SweetTextarea from "@c/form/sweet-textarea";
import { required } from "vuelidate/lib/validators";

export default {
  components: { SweetTextarea },

  data() {
    return {
      assetGroupCount: undefined,
      assetGroupDescription: undefined,
      editedAssetGroup: undefined,
      criticalityLevelDetermination: false,

      filterEnabled: false,
      assetGroupCategoryFilter: undefined,
      noFilter: undefined,
      nameFilter: undefined,

      request: {
        options: { page: 1, limit: 14, sort: { assetGroupCategory: 1, no: 1 } },
        query: {},
      },
    };
  },
  beforeDestroy() {},
  computed: {
    pagination() {
      return this.$store.getters[`organizationAssetGroup/pagination`];
    },
    isLoading() {
      return this.$store.getters[`organizationAssetGroup/isLoading`];
    },
  },
  watch: {
    request: {
      deep: true,
      immediate: true,
      handler() {
        this.debounce(() => this.fetchAssetGroups());
      },
    },
    noFilter(n, o) {
      if (n) {
        const isNumber = !isNaN(n);
        if (!isNumber) {
          this.clearNoFilter();
        }
        this.request.query = { ...this.request.query, ...(isNumber ? { no: { value: n, isObject: true } } : {}) };
      } else {
        this.clearNoFilter();
      }
    },
    nameFilter(n, o) {
      if (n) {
        this.request.query = { ...this.request.query, name: { value: n } };
      } else {
        this.request.query["name"] = null;
        delete this.request.query["name"];
      }
    },
  },
  validations: {
    assetGroupDescription: {
      required,
    },
    assetGroupCount: {
      required,
    },
  },
  methods: {
    clearNoFilter() {
      this.request.query["no"] = null;
      delete this.request.query["no"];
    },
    toggleFilter() {
      this.filterEnabled = !this.filterEnabled;

      if (!this.filterEnabled) {
        this.request = {
          options: { page: 1, limit: 14, sort: { assetGroupCategory: 1, no: 1 } },
          query: {},
        };
        this.nameFilter = undefined;
        this.categoryFilter = undefined;
      }
    },
    async onAssetGroupCategorySearch(value, callback) {
      const response = await this.$store.dispatch(`organizationAssetGroupCategory/paginate`, {
        options: { limit: 200 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },
    onAssetGroupCategorySelect(assetGroupCategory) {
      if (!assetGroupCategory) {
        this.request.query["assetGroupCategory"] = undefined;
        delete this.request.query["assetGroupCategory"];
        return;
      }
      this.request.query = {
        ...this.request.query,
        assetGroupCategory: { value: assetGroupCategory._id, isObject: true },
      };
    },
    editAssetGroupCount(item) {
      this.assetGroupCount = item.count;
      this.editedAssetGroup = item;
      this.$root.$emit("bv::show::modal", "modal-add-asset-group-count");
    },
    editAssetGroupDescription(item) {
      this.assetGroupDescription = this.$options.filters.translate(item.description);
      this.editedAssetGroup = item;
      this.$root.$emit("bv::show::modal", "modal-add-asset-group-description");
    },
    async selectBox(item) {
      return this.$store.dispatch("assetGroupBoxes/selectAssetGroup", item);
    },
    redirectToSurvey(item) {
      if (item) {
        this.selectBox(item);
        this.$router.push(`/ddo/asset-group/${item._id}/survey`);
      }
    },
    async saveAssetGroupCount() {
      try {
        await this.$store.dispatch("organizationAssetGroupInventory/updateAssetGroupCount", { id: this.editedAssetGroup._id, count: this.assetGroupCount });

        this.$toast.success(this.$t("common.saved"));
        await this.fetchAssetGroups();
        this.$root.$emit("bv::hide::modal", "modal-add-asset-group-count");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async saveAssetGroupDescription() {
      try {
        await this.$store.dispatch("organizationAssetGroupInventory/updateAssetGroupDescription", { id: this.editedAssetGroup._id, description: this.assetGroupDescription });

        this.$toast.success(this.$t("common.saved"));

        await this.fetchAssetGroups();

        this.$root.$emit("bv::hide::modal", "modal-add-asset-group-description");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async fetchAssetGroups() {
      const payload = { ...this.request };

      if (this.product) {
        payload.query = { ...this.request.query, products: { value: this.product, isObject: true } };
      }
      await this.$store.dispatch("organizationAssetGroup/paginate", payload);
    },
    async downloadExcel() {
      try {
        const payload = { organization: { _id: this.currentOrganization?._id, name: this.currentOrganization?.name } };

        const { data: file } = await this.$store.dispatch(`organizationAssetGroupInventory/download`, payload);
        this.download(file);

        this.$toast.success(this.$t("common.success"));
      } catch (error) {
        console.error(error);
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async download(item) {
      this.$store.commit("fileManager/addDownloadedFile", { file: item });
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("../task/tasks.scss");

tr td {
  text-align: center;
}
</style>
